import { HStack, Select, Skeleton, createListCollection } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type CommitmentPublic, CsrPlansService } from '@/client';

type PillarItem = {
  id: string;
  name: string;
};

type CommitmentItem = {
  id: string;
  name: string;
};

function getPillarQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['pillars'],
    queryFn: async () =>
      await CsrPlansService.readCsrPlan({
        companySlug: slug,
      }),
  };
}

export const SelectCommitment = ({
  value,
  onChange,
  onLine = true,
}: {
  value: string | null;
  onChange: (value: string) => void;
  onLine?: boolean;
}) => {
  const { slug } = useParams({ strict: false });
  const { t } = useTranslation();
  const { data: pillarData, isLoading } = useQuery({
    ...getPillarQueryOptions({ slug }),
  });
  const pillars = pillarData?.pillars || [];

  const [selectedPillarId, setSelectedPillarId] = useState<string[]>([]);
  const [selectedCommitmentId, setSelectedCommitmentId] = useState<string[]>(value ? [value] : []);

  const pillarsCollection = createListCollection({
    items:
      pillars.map((pillar) => ({
        id: pillar.id,
        name: pillar.name ?? '',
      })) || [],
    itemToString: (item: PillarItem) => item.name,
    itemToValue: (item: PillarItem) => item.id,
  });

  const commitmentCollection = createListCollection({
    items:
      pillars
        .reduce((commitments, currentPillar): CommitmentPublic[] => {
          if (selectedPillarId.length > 0) {
            if (selectedPillarId[0] === currentPillar.id) {
              return commitments.concat(currentPillar.commitments);
            }
            return commitments;
          }
          return commitments.concat(currentPillar.commitments);
        }, [] as CommitmentPublic[])
        .map((commitment: CommitmentPublic) => ({
          id: commitment.id,
          name: commitment.name ?? '',
        })) || [],
    itemToString: (item: CommitmentItem) => item.name ?? '',
    itemToValue: (item: CommitmentItem) => item.id,
  });

  useEffect(() => {
    if (selectedCommitmentId.length > 0) {
      const found = pillars.find((pillar) => pillar.commitments.some((c) => c.id === selectedCommitmentId[0]));
      if (found) {
        setSelectedPillarId([found.id]);
      }
    }
  }, [selectedCommitmentId, pillarData]);

  return isLoading ? (
    <Skeleton height="8" />
  ) : (
    <HStack gap={4} w={'100%'} flexWrap={onLine ? 'nowrap' : 'wrap'}>
      <Select.Root
        collection={pillarsCollection}
        value={selectedPillarId}
        onValueChange={(e) => {
          setSelectedPillarId(e.value);
          setSelectedCommitmentId([]);
        }}
        positioning={{ sameWidth: true }}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText placeholder={t('indicator.filter.pillar.select')} />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.ClearTrigger />
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Select.Positioner>
          <Select.Content>
            {pillarData?.pillars.map((pillar) => (
              <Select.Item item={pillar} key={pillar.id}>
                {pillar.name}
                <Select.ItemIndicator />
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>

      <Select.Root
        collection={commitmentCollection}
        value={selectedCommitmentId}
        onValueChange={(e) => {
          setSelectedCommitmentId(e.value);
          onChange(e.value[0]);
        }}
        positioning={{ sameWidth: true }}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText placeholder={t('indicator.filter.commitment.select')} />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.ClearTrigger />
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Select.Positioner>
          <Select.Content>
            {commitmentCollection.items.map((commitment) => (
              <Select.Item item={commitment} key={commitment.id}>
                {commitment.name}
                <Select.ItemIndicator />
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
    </HStack>
  );
};
